import { useLiveQuery } from "@tanstack/react-db";
import { scriptsCollection } from "@/lib/collections/scripts-collection";
import { pagesCollection, settleRow, trackPageMutation } from "@/lib/collections/pages-collection";
import { useAllPages } from "@/lib/collections/use-script-pages";
import { blobStore } from "@/lib/blob-store";
import { countPages, displayLabel, type ScriptSummary } from "@/lib/script-summary";
import { scriptMetadataPayload, type Script, type StudentGender } from "@/lib/script";
import { httpStore } from "@/lib/api/instance";
import { getQueue } from "@/lib/queue-store";

export interface NewScriptInput {
  label: string | null;
  rollNumber: string | null;
  studentName: string | null;
  gender: StudentGender | null;
  subject: string | null;
  section: string | null;
  groundTruthMarksTotal: number | null;
  groundTruthPerQuestionJson: string | null;
}

export function emptyScriptInput(): NewScriptInput {
  return {
    label: null,
    rollNumber: null,
    studentName: null,
    gender: null,
    subject: null,
    section: null,
    groundTruthMarksTotal: null,
    groundTruthPerQuestionJson: null,
  };
}

export function createScript(input: NewScriptInput): Script {
  const script: Script = {
    id: crypto.randomUUID(),
    label: input.label,
    createdAt: Date.now(),
    uploadEnabled: true,
    remoteId: null,
    rollNumber: input.rollNumber,
    studentName: input.studentName,
    gender: input.gender,
    subject: input.subject,
    section: input.section,
    groundTruthMarksTotal: input.groundTruthMarksTotal,
    groundTruthPerQuestionJson: input.groundTruthPerQuestionJson,
  };
  scriptsCollection.insert(script);
  return script;
}

import { addPageFromBlob as insertPageFromBlob, type AddPageOptions } from "@/lib/page-persist";

export function addPageFromBlob(scriptId: string, blob: Blob, options?: AddPageOptions): string {
  const id = insertPageFromBlob(scriptId, blob, undefined, options);
  void getQueue()?.runOnce();
  return id;
}

/** Tracked and awaited, not fire-and-forget: a retake deletes this row and
 * immediately inserts its replacement under a different key, and an
 * unsettled prior write makes @tanstack/db drop that insert (see
 * trackPageMutation). */
export async function deletePage(page: { id: string; blobKey: string }): Promise<void> {
  trackPageMutation(page.id, pagesCollection.delete(page.id));
  await settleRow(page.id);
  await blobStore.delete(page.blobKey);
}

export async function deleteScriptAndPages(scriptId: string): Promise<void> {
  const pages = pagesCollection.toArray.filter(p => p.scriptId === scriptId);
  for (const page of pages) {
    pagesCollection.delete(page.id);
    await blobStore.delete(page.blobKey);
  }
  scriptsCollection.delete(scriptId);
}

/** Edits the metadata captured at creation — id, createdAt, and
 * uploadEnabled are untouched. If the script has already reached the
 * server (remoteId set), the edit is pushed there too; otherwise the next
 * upload's ensureScript call carries the current metadata anyway. */
export function updateScript(id: string, input: NewScriptInput): void {
  let remoteId: string | null = null;
  scriptsCollection.update(id, draft => {
    draft.label = input.label;
    draft.rollNumber = input.rollNumber;
    draft.studentName = input.studentName;
    draft.gender = input.gender;
    draft.subject = input.subject;
    draft.section = input.section;
    draft.groundTruthMarksTotal = input.groundTruthMarksTotal;
    draft.groundTruthPerQuestionJson = input.groundTruthPerQuestionJson;
    remoteId = draft.remoteId;
  });
  if (remoteId) {
    const script = scriptsCollection.get(id);
    if (script) {
      httpStore.patchScriptMetadata(id, scriptMetadataPayload(script)).catch(e => {
        console.error(`failed to sync script metadata ${id}`, e);
      });
    }
  }
}

export function useScriptSummaries(): { summaries: ScriptSummary[]; isLoading: boolean } {
  const { data: scripts, isLoading: scriptsLoading } = useLiveQuery({
    query: q => q.from({ script: scriptsCollection }),
  });
  const pages = useAllPages();

  const summaries = scripts
    .map(script => {
      const scriptPages = pages.filter(p => p.scriptId === script.id);
      return {
        script,
        pages: scriptPages,
        counts: countPages(scriptPages),
        displayLabel: displayLabel(script),
      };
    })
    .sort((a, b) => b.script.createdAt - a.script.createdAt);

  return { summaries, isLoading: scriptsLoading };
}
